import { recordActivity } from "../../auditActivity";
import { useEffect, useMemo, useState, useCallback } from "react";
import { functions } from "../../firebase";
import { httpsCallable } from "firebase/functions";

// ─── Local styling (mirrors the app theme, self-contained) ───────────────────
function useStyles(theme) {
  return {
    card: { background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 14, overflow: "hidden" },
    btn: { padding: "8px 14px", borderRadius: 8, border: "none", background: theme.primary, color: "#fff", cursor: "pointer", fontWeight: 600, fontFamily: "'DM Sans',sans-serif", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 },
    btn2: { padding: "8px 14px", borderRadius: 8, border: `1px solid ${theme.borderInput || theme.border}`, background: theme.surface, color: theme.primary, cursor: "pointer", fontWeight: 600, fontFamily: "'DM Sans',sans-serif", fontSize: 13 },
    th: { textAlign: "left", padding: "10px 14px", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: theme.textMuted, whiteSpace: "nowrap" },
    td: { padding: "12px 14px", fontSize: 13, color: theme.text, verticalAlign: "middle" },
  };
}

const fmtMoney = (amount, currency) => {
  try { return new Intl.NumberFormat("en-GB", { style: "currency", currency: currency || "GBP" }).format(amount || 0); }
  catch { return `${(amount || 0).toFixed(2)}`; }
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

export default function InvoicesModule({ user, lodges = [], notify, theme }) {
  const T = theme;
  const s = useStyles(theme);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [downloading, setDownloading] = useState("");

  // Show a Lodge column only for treasurers who hold more than one lodge.
  const multiLodge = useMemo(() => {
    const nums = new Set(invoices.map((i) => String(i.lodgeNumber || "")).filter(Boolean));
    return nums.size > 1;
  }, [invoices]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const fn = httpsCallable(functions, "getLodgeInvoices");
      const res = await fn({});
      const list = (res && res.data && res.data.invoices) || [];
      // Newest first (server already orders, but be safe).
      list.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
      setInvoices(list);
    } catch (e) {
      console.error(e);
      setError((e && e.message) || "Could not load your invoices.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const download = async (inv) => {
    setDownloading(inv.invoiceID);
    try {
      const fn = httpsCallable(functions, "getInvoicePdf");
      const res = await fn({ invoiceId: inv.invoiceID });
      const b64 = res && res.data && res.data.pdfBase64;
      if (!b64) { notify && notify("No PDF available for this invoice", "error"); return; }
      const bytes = atob(b64);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
      const blob = new Blob([arr], { type: "application/pdf" });
      void recordActivity("invoice.download",{id:String(inv.invoiceID || "")});
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Invoice-${inv.invoiceNumber || inv.invoiceID}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      notify && notify((e && e.message) || "Could not download the invoice PDF", "error");
    } finally {
      setDownloading("");
    }
  };

  const statusBadge = (inv) => {
    const paid = (inv.amountDue || 0) <= 0 && (inv.status === "PAID" || (inv.amountPaid || 0) > 0);
    if (paid) return <span style={{ fontSize: 11, fontWeight: 700, color: T.success, background: T.successBg, padding: "3px 10px", borderRadius: 12 }}>Paid</span>;
    if (inv.status === "VOIDED" || inv.status === "DELETED") return <span style={{ fontSize: 11, fontWeight: 700, color: T.textMuted, background: T.surfaceAlt, padding: "3px 10px", borderRadius: 12 }}>{inv.status.charAt(0) + inv.status.slice(1).toLowerCase()}</span>;
    return <span style={{ fontSize: 11, fontWeight: 700, color: T.warning, background: T.warningBg, padding: "3px 10px", borderRadius: 12 }}>Awaiting payment</span>;
  };

  const totalOutstanding = useMemo(() => invoices.reduce((sum, i) => sum + (i.amountDue || 0), 0), [invoices]);
  const currency = invoices[0]?.currency || "GBP";

  return (
    <div style={{ animation: "fadeUp .4s ease" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 22, flexWrap: "wrap", gap: 10 }}>
        <div>
          <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 26, color: T.text, margin: 0 }}>My Invoices</h1>
          <p style={{ color: T.textMuted, margin: "4px 0 0", fontSize: 13 }}>Service-charge invoices for your lodge, straight from Xero.</p>
        </div>
        <button onClick={load} disabled={loading} style={{ ...s.btn2, opacity: loading ? 0.6 : 1 }}>{loading ? "Refreshing…" : "Refresh"}</button>
      </div>

      {!loading && !error && invoices.length > 0 && (
        <div style={{ display: "flex", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
          <div style={{ ...s.card, padding: "12px 18px", flex: "0 0 auto" }}>
            <div style={{ fontSize: 11, color: T.textMuted, textTransform: "uppercase", fontWeight: 700 }}>Invoices</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: T.text }}>{invoices.length}</div>
          </div>
          <div style={{ ...s.card, padding: "12px 18px", flex: "0 0 auto" }}>
            <div style={{ fontSize: 11, color: T.textMuted, textTransform: "uppercase", fontWeight: 700 }}>Outstanding</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: totalOutstanding > 0 ? T.warning : T.success }}>{fmtMoney(totalOutstanding, currency)}</div>
          </div>
        </div>
      )}

      <div style={s.card}>
        {loading && <div style={{ padding: 40, textAlign: "center", color: T.textMuted, fontSize: 14 }}>Loading your invoices…</div>}

        {!loading && error && (
          <div style={{ padding: 28, textAlign: "center" }}>
            <div style={{ color: T.danger, fontWeight: 600, marginBottom: 6 }}>Couldn't load invoices</div>
            <div style={{ color: T.textMuted, fontSize: 13, marginBottom: 16 }}>{error}</div>
            <button onClick={load} style={s.btn}>Try again</button>
          </div>
        )}

        {!loading && !error && invoices.length === 0 && (
          <div style={{ padding: 40, textAlign: "center", color: T.textMuted, fontSize: 14 }}>
            No invoices yet. Once an order has been sent to the kitchen and invoiced, it'll appear here.
          </div>
        )}

        {!loading && !error && invoices.length > 0 && (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${T.border}`, background: T.surfaceAlt }}>
                  <th style={s.th}>Invoice</th>
                  <th style={s.th}>Date</th>
                  <th style={s.th}>Due</th>
                  {multiLodge && <th style={s.th}>Lodge</th>}
                  <th style={{ ...s.th, textAlign: "right" }}>Total</th>
                  <th style={{ ...s.th, textAlign: "right" }}>Outstanding</th>
                  <th style={s.th}>Status</th>
                  <th style={{ ...s.th, textAlign: "right" }}>PDF</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.invoiceID} style={{ borderBottom: `1px solid ${T.surfaceAlt}` }}>
                    <td style={{ ...s.td, fontWeight: 700 }}>{inv.invoiceNumber || "—"}{inv.reference ? <div style={{ fontSize: 11, fontWeight: 400, color: T.textMuted }}>{inv.reference}</div> : null}</td>
                    <td style={s.td}>{fmtDate(inv.date)}</td>
                    <td style={s.td}>{fmtDate(inv.dueDate)}</td>
                    {multiLodge && <td style={s.td}>{inv.lodgeName || `#${inv.lodgeNumber}`}</td>}
                    <td style={{ ...s.td, textAlign: "right", fontWeight: 600 }}>{fmtMoney(inv.total, inv.currency)}</td>
                    <td style={{ ...s.td, textAlign: "right", color: (inv.amountDue || 0) > 0 ? T.warning : T.textMuted }}>{fmtMoney(inv.amountDue, inv.currency)}</td>
                    <td style={s.td}>{statusBadge(inv)}</td>
                    <td style={{ ...s.td, textAlign: "right" }}>
                      <button onClick={() => download(inv)} disabled={downloading === inv.invoiceID} title="Download PDF" style={{ ...s.btn, padding: "7px 12px", opacity: downloading === inv.invoiceID ? 0.6 : 1 }}>
                        {downloading === inv.invoiceID ? "…" : "Download"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p style={{ fontSize: 11, color: T.textMuted, marginTop: 14, lineHeight: 1.5 }}>
        Invoices are read live from Xero each time you open this page, so they're always up to date. If you think an invoice is missing, contact your administrator.
      </p>
    </div>
  );
}
